import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";

import { cn } from "@/lib/utils";
import { toastUtil } from "@/utils/toastUtil";
import { fileToBase64 } from "@/utils/fileToBase64";
import { useMyCompanyDocuments } from "@/hooks/queries/useMyCompanyDocuments";
import { useUploadMyCompanyDocument } from "@/hooks/mutations/useMyCompanyDocumentMutations";
import { useCanAutoQualify } from "@/hooks/useCanAutoQualify";
import {
  AnalyzeWorksCard,
  UnlockedWorksCard,
} from "@/components/settings/AnalyzeWorksCard";
import {
  CompanyDocumentRow,
  CompanyDocumentSection,
} from "@/components/settings/CompanyDocumentSection";

const CARD =
  "rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

// Alineado con MAX_UPLOAD_BYTES del backend: el archivo viaja en base64.
const MAX_FILE_BYTES = 40 * 1024 * 1024;
const ACCEPTED = ".pdf,.jpg,.jpeg,.png";

/**
 * Configuración → Documentos: lo que la empresa sube para que el cruce sepa qué
 * acredita. Se sube todo a una misma zona y la IA decide la categoría de cada
 * archivo al leerlo; la página solo muestra dónde cayó cada uno.
 */
export default function DocumentsSettings() {
  const canAutoQualify = useCanAutoQualify();
  const { data, isLoading, isError } = useMyCompanyDocuments();
  const upload = useUploadMyCompanyDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  /** Sube de a uno: son pocos archivos y así el error de cada uno sale solo. */
  async function uploadFiles(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_BYTES) {
        toastUtil(`${file.name} pesa más de 40 MB.`, "error");
        continue;
      }
      setUploadingName(file.name);
      try {
        await upload.mutateAsync({
          fileName: file.name,
          base64Data: await fileToBase64(file),
        });
      } catch {
        // El toast ya lo muestra la mutation; se sigue con el siguiente.
      }
    }
    setUploadingName(null);
  }

  if (isLoading) {
    return (
      <div className={cn(CARD, "p-6 text-sm text-slate-400")}>
        Cargando documentos…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className={cn(CARD, "p-6 text-sm text-slate-500")}>
        No se pudieron leer los documentos de tu empresa. Intenta de nuevo en un
        momento.
      </div>
    );
  }

  const progress = Math.round((data.loadedCount / data.totalCount) * 100);
  const allDocuments = [
    ...data.slots.flatMap((slot) => slot.documents),
    ...data.otherDocuments,
    ...data.unreadDocuments,
  ];
  const needAttention = allDocuments.filter((doc) =>
    ["EXPIRED", "EXPIRING", "FAILED"].includes(doc.state),
  );

  return (
    <div className="flex flex-col gap-4">
      {canAutoQualify ? (
        <AnalyzeWorksCard documents={data} />
      ) : (
        <UnlockedWorksCard documents={data} />
      )}

      <div className={cn(CARD, "p-6")}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
              Documentos de la empresa
            </h2>
            <p className="mt-1 max-w-lg text-xs text-slate-500">
              Se usan para cruzarlos contra los requisitos de cada licitación.
              Súbelos una vez y sirven para todas.
            </p>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-2xl font-extrabold text-slate-800 dark:text-slate-100">
              {progress}%
            </div>
            <div className="text-[11px] text-slate-400">
              {data.loadedCount} de {data.totalCount} categorías con documentos
            </div>
          </div>
        </div>

        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
        </div>

        {data.canManage ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              uploadFiles(event.dataTransfer.files);
            }}
            disabled={uploadingName !== null}
            className={cn(
              "mt-5 flex w-full cursor-pointer flex-col items-center gap-1 rounded-xl border border-dashed px-4 py-6 text-center transition-colors disabled:cursor-wait",
              dragging
                ? "border-primary bg-primary/5"
                : "hover:bg-slate-50 dark:hover:bg-slate-800/40",
            )}
          >
            {uploadingName ? (
              <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                <Loader2 className="size-4 animate-spin" />
                Subiendo {uploadingName}…
              </span>
            ) : (
              <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                <Upload className="size-4" />
                Arrastra tus archivos o haz clic para subir
              </span>
            )}
            <span className="text-[11px] text-slate-400">
              PDF, JPG o PNG · hasta 40 MB por archivo · la IA los lee y los
              ordena por categoría
            </span>
          </button>
        ) : (
          <p className="mt-5 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-800/40">
            Solo el administrador de tu organización puede subir o quitar
            documentos.
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          hidden
          onChange={(event) => {
            if (event.target.files) uploadFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </div>

      {/* Sin tipo todavía: la categoría sale de la lectura. */}
      {data.unreadDocuments.length > 0 && (
        <div className={cn(CARD, "flex flex-col gap-2 p-5")}>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
            Recién subidos
          </div>
          <div className="mb-1 text-xs text-slate-400">
            La IA los está leyendo para ubicarlos en su categoría. Tarda alrededor
            de un minuto por archivo.
          </div>
          {data.unreadDocuments.map((document) => (
            <CompanyDocumentRow
              key={document.id}
              document={document}
              canManage={data.canManage}
            />
          ))}
        </div>
      )}

      {data.slots.map((slot) => (
        <CompanyDocumentSection
          key={slot.type}
          title={slot.title}
          hint={slot.hint}
          state={slot.state}
          documents={slot.documents}
          canManage={data.canManage}
        />
      ))}

      {data.otherDocuments.length > 0 && (
        <CompanyDocumentSection
          title="Otros documentos"
          hint="Financieros y el resto: no cuentan para el avance, pero sí entran al cruce."
          state={null}
          documents={data.otherDocuments}
          canManage={data.canManage}
        />
      )}

      {needAttention.length > 0 && (
        <div className={cn(CARD, "px-5 py-4 text-xs text-slate-500")}>
          {needAttention.length === 1
            ? "1 documento necesita atención: "
            : `${needAttention.length} documentos necesitan atención: `}
          {needAttention.map((doc) => doc.title).join(", ")}.
        </div>
      )}
    </div>
  );
}
